/**
 * Pure helpers for the Polish-§2 drag-and-drop reorder logic in
 * TenderEstimate.jsx. Keeps the component file lean and the move logic
 * independently testable.
 *
 * Item ID conventions (we use them as both DnD ids and lookup keys):
 *   phase-{phaseId}                      — a phase
 *   sub-{subPhaseId}                     — a sub-phase
 *   line-{lineId}                        — a line item
 *
 * Container IDs (where items can be dropped, primarily for empty-container
 * landings):
 *   phases-root                          — the top-level phase list
 *   phase-{phaseId}-subphases            — sub-phases within phase P
 *   phase-{phaseId}-root-lines           — phase-root line items of phase P
 *   subphase-{subPhaseId}-lines          — line items inside sub-phase SP
 */

export const ID = {
  phase: (id) => `phase-${id}`,
  sub:   (id) => `sub-${id}`,
  line:  (id) => `line-${id}`,
  phasesRoot:        () => "phases-root",
  phaseSubphases:    (phaseId) => `phase-${phaseId}-subphases`,
  phaseRootLines:    (phaseId) => `phase-${phaseId}-root-lines`,
  subphaseLines:     (subId)   => `subphase-${subId}-lines`,
};

/** Parse a DnD id back into { kind, id, container? }. */
export function parseId(domId) {
  if (!domId) return null;
  const s = String(domId);
  if (s === "phases-root") return { kind: "container", container: "phases-root" };
  if (s.startsWith("phase-") && s.endsWith("-subphases")) {
    return { kind: "container", container: s, phaseId: s.slice("phase-".length, -"-subphases".length) };
  }
  if (s.startsWith("phase-") && s.endsWith("-root-lines")) {
    return { kind: "container", container: s, phaseId: s.slice("phase-".length, -"-root-lines".length) };
  }
  if (s.startsWith("subphase-") && s.endsWith("-lines")) {
    return { kind: "container", container: s, subPhaseId: s.slice("subphase-".length, -"-lines".length) };
  }
  if (s.startsWith("phase-")) return { kind: "phase", id: s.slice("phase-".length) };
  if (s.startsWith("sub-"))   return { kind: "sub",   id: s.slice("sub-".length) };
  if (s.startsWith("line-"))  return { kind: "line",  id: s.slice("line-".length) };
  return null;
}

/** Locate a line in the phases tree. Returns { phaseId, subPhaseId | null,
 *  index, line } or null if not found. */
export function findLine(phases, lineId) {
  for (const p of phases || []) {
    const i = (p.line_items || []).findIndex((l) => l.id === lineId);
    if (i !== -1) return { phaseId: p.id, subPhaseId: null, index: i, line: p.line_items[i] };
    for (const sp of p.sub_phases || []) {
      const j = (sp.line_items || []).findIndex((l) => l.id === lineId);
      if (j !== -1) return { phaseId: p.id, subPhaseId: sp.id, index: j, line: sp.line_items[j] };
    }
  }
  return null;
}

/** Locate a sub-phase. Returns { phaseId, index } or null. */
export function findSubPhase(phases, subId) {
  for (const p of phases || []) {
    const i = (p.sub_phases || []).findIndex((sp) => sp.id === subId);
    if (i !== -1) return { phaseId: p.id, index: i };
  }
  return null;
}

/** Move an item within an array — pure. Returns a new array. */
export function arrMove(arr, fromIdx, toIdx) {
  if (fromIdx === toIdx) return arr.slice();
  const out = arr.slice();
  const [item] = out.splice(fromIdx, 1);
  out.splice(toIdx, 0, item);
  return out;
}

/** Reorder phases (top-level). */
export function reorderPhases(phases, fromIdx, toIdx) {
  return arrMove(phases, fromIdx, toIdx);
}

/** Move a sub-phase to a new (phaseId, index) position. Cross-phase if the
 *  destination phaseId differs from the source. Pure — returns new phases.
 *  When toIndex is null we append. */
export function moveSubPhase(phases, subId, toPhaseId, toIndex) {
  const found = findSubPhase(phases, subId);
  if (!found) return phases;
  const { phaseId: fromPhaseId, index: fromIdx } = found;

  // Take the sub-phase out
  const next = phases.map((p) => p.id === fromPhaseId
    ? { ...p, sub_phases: (p.sub_phases || []).filter((sp) => sp.id !== subId) }
    : p
  );
  const sub = phases.find((p) => p.id === fromPhaseId).sub_phases.find((sp) => sp.id === subId);

  // Same-phase move: clamp the destination index against the new (smaller) array
  return next.map((p) => {
    if (p.id !== toPhaseId) return p;
    const list = p.sub_phases || [];
    const idx = toIndex == null ? list.length : Math.max(0, Math.min(toIndex, list.length));
    const out = list.slice();
    out.splice(idx, 0, sub);
    return { ...p, sub_phases: out };
  });
}

/** Move a line to a new (phaseId, subPhaseId|null, index) position.
 *  Cross-container moves supported. When toIndex is null we append. */
export function moveLine(phases, lineId, toPhaseId, toSubPhaseId, toIndex) {
  const found = findLine(phases, lineId);
  if (!found) return phases;
  const { phaseId: fromPhaseId, subPhaseId: fromSubId, line } = found;

  // 1) Remove from source container
  const stripped = phases.map((p) => {
    if (p.id !== fromPhaseId) return p;
    if (fromSubId == null) {
      return { ...p, line_items: (p.line_items || []).filter((l) => l.id !== lineId) };
    }
    return {
      ...p,
      sub_phases: (p.sub_phases || []).map((sp) => sp.id === fromSubId
        ? { ...sp, line_items: (sp.line_items || []).filter((l) => l.id !== lineId) }
        : sp
      )
    };
  });

  // 2) Insert into destination
  return stripped.map((p) => {
    if (p.id !== toPhaseId) return p;
    if (toSubPhaseId == null) {
      const list = p.line_items || [];
      const idx = toIndex == null ? list.length : Math.max(0, Math.min(toIndex, list.length));
      const out = list.slice();
      out.splice(idx, 0, line);
      return { ...p, line_items: out };
    }
    return {
      ...p,
      sub_phases: (p.sub_phases || []).map((sp) => {
        if (sp.id !== toSubPhaseId) return sp;
        const list = sp.line_items || [];
        const idx = toIndex == null ? list.length : Math.max(0, Math.min(toIndex, list.length));
        const out = list.slice();
        out.splice(idx, 0, line);
        return { ...sp, line_items: out };
      })
    };
  });
}

/** Resolve a drop target id (item or container) into a destination
 *  for line moves. Returns { toPhaseId, toSubPhaseId, toIndex } or null
 *  if the target isn't a valid line drop site. */
export function resolveLineDropTarget(phases, overId) {
  const meta = parseId(overId);
  if (!meta) return null;

  // Dropped on another LINE — insert at that line's position in its container
  if (meta.kind === "line") {
    const found = findLine(phases, meta.id);
    if (!found) return null;
    return { toPhaseId: found.phaseId, toSubPhaseId: found.subPhaseId, toIndex: found.index };
  }
  // Dropped on a CONTAINER — append to that container
  if (meta.kind === "container") {
    if (meta.container.endsWith("-root-lines")) {
      return { toPhaseId: meta.phaseId, toSubPhaseId: null, toIndex: null };
    }
    if (meta.container.startsWith("subphase-") && meta.container.endsWith("-lines")) {
      // Need to find the parent phase of this sub-phase
      const sp = findSubPhase(phases, meta.subPhaseId);
      if (!sp) return null;
      return { toPhaseId: sp.phaseId, toSubPhaseId: meta.subPhaseId, toIndex: null };
    }
    return null;
  }
  return null;
}

/** Resolve a drop target id for sub-phase moves. */
export function resolveSubPhaseDropTarget(phases, overId) {
  const meta = parseId(overId);
  if (!meta) return null;
  if (meta.kind === "sub") {
    const found = findSubPhase(phases, meta.id);
    if (!found) return null;
    return { toPhaseId: found.phaseId, toIndex: found.index };
  }
  if (meta.kind === "container" && meta.container.endsWith("-subphases")) {
    return { toPhaseId: meta.phaseId, toIndex: null }; // append
  }
  return null;
}
